import moment from "moment";
import TimestampConverter from "../helpers/TimestampConverter.jsx";
import zoneConverter from "../helpers/zoneConverter.jsx";
import { convertTo24HourFormatback } from "./timeFormat.js";
import { formatDuration } from "./uiHelpers.js";

// Helper to handle date conversion - supports both zoneConverter and moment.tz
const convertDate = (date, useMomentTz = false) => {
  if (!date) return null;
  if (useMomentTz) {
    return moment.tz(date, "YYYY-MM-DD HH:mm:ss", global.config.getTimeZone()).format();
  }
  return zoneConverter(date, global.config.getTimeZone(), "YYYY-MM-DD HH:mm:ss");
};

/**
 * Create setters object for edit state initialization
 * This helper reduces code duplication when calling initializeEditState
 */
export const createEditStateSetters = (setters) => ({
  setEditedId: setters.setEditedId,
  setTaskNameEdit: setters.setTaskNameEdit,
  setProjectIdEdit: setters.setProjectIdEdit,
  setProjectNameEdit: setters.setProjectNameEdit,
  setSelectedUser: setters.setSelectedUser,
  setSprintNameEdit: setters.setSprintNameEdit,
  setSprintIdEdit: setters.setSprintIdEdit,
  setDateEdit: setters.setDateEdit,
  setTagIdEdit: setters.setTagIdEdit,
  setStartTimeEdit: setters.setStartTimeEdit,
  setEndTimeEdit: setters.setEndTimeEdit,
  setStartTimeTemp: setters.setStartTimeTemp,
  setEndTimeTemp: setters.setEndTimeTemp,
  setStartTimeEdit24: setters.setStartTimeEdit24,
  setEndTimeEdit24: setters.setEndTimeEdit24,
  setClientEdit: setters.setClientEdit,
  setClientIdEdit: setters.setClientIdEdit,
  setBillableEdit: setters.setBillableEdit,
  setUFormatedTime: setters.setUFormatedTime,
  setUDuration: setters.setUDuration,
  setUStartTimeError: setters.setUStartTimeError,
  setUEndTimeError: setters.setUEndTimeError,
  setUFormatedTimeError: setters.setUFormatedTimeError,
  setProjectEditDropdownOpen: setters.setProjectEditDropdownOpen,
  setTagEditDropdownOpen: setters.setTagEditDropdownOpen,
});

/**
 * Initialize edit state for a group or row entry
 * @param {Object} entry - The group or row entry to initialize edit state from
 * @param {Object} setters - Object containing all setter functions
 * @param {Function} formatTime - Function to format time values
 */
export const initializeEditState = (entry, setters, formatTime) => {
  const {
    setEditedId,
    setTaskNameEdit,
    setProjectIdEdit,
    setProjectNameEdit,
    setSelectedUser,
    setSprintNameEdit,
    setSprintIdEdit,
    setDateEdit,
    setTagIdEdit,
    setStartTimeEdit,
    setEndTimeEdit,
    setStartTimeTemp,
    setEndTimeTemp,
    setStartTimeEdit24,
    setEndTimeEdit24,
    setClientEdit,
    setClientIdEdit,
    setBillableEdit,
    setUFormatedTime,
    setUDuration,
    setUStartTimeError,
    setUEndTimeError,
    setUFormatedTimeError,
    setProjectEditDropdownOpen,
    setTagEditDropdownOpen,
  } = setters;

  // Determine if entry is a group or row
  const isGroup = entry.entries && Array.isArray(entry.entries);
  const data = entry;
  const duration = isGroup ? data.totalDuration : data.duration;

  setEditedId(data._id);
  setProjectEditDropdownOpen(false);
  setTagEditDropdownOpen(false);
  setTaskNameEdit(data.task);
  setProjectIdEdit(data.project._id);
  setProjectNameEdit(data.project.project_name);
  setSelectedUser(data.user);

  if (data.sprint) {
    setSprintNameEdit(data.sprint.sprint_name);
    setSprintIdEdit(data.sprint._id);
  } else {
    setSprintIdEdit("");
    setSprintNameEdit("");
  }

  // Handle date conversion - use zoneConverter for consistency
  if (data.date) {
    setDateEdit(convertDate(data.date));
  }

  setTagIdEdit(data.tag && data.tag._id);
  
  const startTime = TimestampConverter(data.startTime);
  const endTime = TimestampConverter(data.endTime);
  
  setStartTimeEdit(startTime);
  setEndTimeEdit(endTime);
  setStartTimeTemp(startTime);
  setEndTimeTemp(endTime);
  setStartTimeEdit24(convertTo24HourFormatback(startTime));
  setEndTimeEdit24(convertTo24HourFormatback(endTime));

  setClientEdit(data.client);
  setClientIdEdit(data.clientId);
  setBillableEdit(data.billable);

  setUFormatedTime(formatDuration(duration, formatTime));
  setUDuration(duration);
  setUStartTimeError(false);
  setUEndTimeError(false);
  setUFormatedTimeError(false);
};

/**
 * Reset edit state to initial values
 * @param {Object} setters - Object containing setter functions
 */
export const resetEditState = (setters) => {
  const {
    setEditedId,
    setTaskNameEdit,
    setProjectIdEdit,
    setProjectNameEdit,
    setSelectedUser,
    setSprintNameEdit,
    setSprintIdEdit,
    setDateEdit,
    setTagIdEdit,
    setStartTimeEdit,
    setEndTimeEdit,
    setClientEdit,
    setClientIdEdit,
    setBillableEdit,
    setUFormatedTime,
    setUDuration,
  } = setters;

  setEditedId(null);
  setTaskNameEdit("");
  setProjectIdEdit("");
  setProjectNameEdit("");
  setSelectedUser(null);
  setSprintNameEdit("");
  setSprintIdEdit("");
  setDateEdit(null);
  setTagIdEdit(null);
  setStartTimeEdit("");
  setEndTimeEdit("");
  setClientEdit(null);
  setClientIdEdit(null);
  setBillableEdit(false);
  setUFormatedTime("");
  setUDuration(null);
};

/**
 * Create a click handler for group/row entries
 * @param {Object} entry - The group or row entry
 * @param {number} groupIndex - Index of the group (for groups only)
 * @param {Function} initializeEditState - Function to initialize edit state
 * @param {Object} editStateSetters - Object containing all setter functions
 * @param {Function} formatTime - Function to format time values
 * @param {Function} toggleGroup - Function to toggle group expansion (for groups only)
 * @returns {Function} Click handler function
 */
export const createGroupClickHandler = (
  entry,
  groupIndex,
  initializeEditState,
  editStateSetters,
  formatTime,
  toggleGroup
) => {
  return () => {
    const isGroup = entry.entries && Array.isArray(entry.entries);
    if (isGroup && entry.entries.length === 1) {
      initializeEditState(entry, editStateSetters, formatTime);
    } else if (isGroup) {
      toggleGroup(groupIndex);
    } else {
      initializeEditState(entry, editStateSetters, formatTime);
    }
  };
};
